import { PERMISSIONS } from '@nks/shared';
import { useQuery } from '@tanstack/react-query';
import { Card, Col, Flex, Row, Select, Skeleton, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import { useHasPermission } from '../auth/session';
import { fetchOperations, type OperationsMetric, type OperationsSummary } from './api';
import { OperationsRowsDrawer, humanise } from './OperationsRowsDrawer';

const { Text, Title } = Typography;

interface Figure {
  id: string;
  label: string;
  value: string;
  line?: string;
  /** Omitted for a figure with no DMS rows behind it (AutoCount's). */
  metric?: OperationsMetric;
  /** Something is waiting too long: shown in the warning colour when above zero. */
  warn?: boolean;
}

/**
 * Insights, Operations (spec 2026-10-27): the DMS's own work this month and
 * what is waiting now, for one branch or all. Every figure with rows behind it
 * opens them. AutoCount's figure sits beside the DMS's and is never added to it.
 */
export function OperationsTab(): ReactNode {
  const { t } = useTranslation();
  const canViewAll = useHasPermission(PERMISSIONS.BRANCH_VIEW_ALL);
  const [branch, setBranch] = useState<string | null>(null);
  const [open, setOpen] = useState<{ metric: OperationsMetric; title: string } | null>(null);

  const query = useQuery({
    queryKey: ['analytics', 'operations', branch],
    queryFn: () => fetchOperations(branch),
  });

  const figure = (f: Figure): ReactNode => {
    const clickable = f.metric !== undefined;
    const openIt = (): void => {
      if (f.metric !== undefined) setOpen({ metric: f.metric, title: f.label });
    };
    return (
      <Col key={f.id} xs={12} md={8} xl={6}>
        <Card
          size="small"
          hoverable={clickable}
          data-testid={`op-${f.id}`}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          aria-label={clickable ? `${f.label}: ${f.value}` : undefined}
          style={{ height: '100%', cursor: clickable ? 'pointer' : 'default' }}
          onClick={openIt}
          onKeyDown={(e) => {
            if (clickable && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              openIt();
            }
          }}
        >
          <Flex vertical gap={2}>
            <Text type="secondary">{f.label}</Text>
            <Title
              level={4}
              className="tabular"
              type={f.warn === true && f.value !== '0' ? 'warning' : undefined}
              style={{ margin: 0 }}
            >
              {f.value}
            </Title>
            {f.line !== undefined && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {f.line}
              </Text>
            )}
          </Flex>
        </Card>
      </Col>
    );
  };

  const section = (id: string, figures: Figure[], empty: boolean, extra?: ReactNode): ReactNode => (
    <Card key={id} size="small" title={t(`insights.operations.${id}.title`)} data-testid={`op-section-${id}`}>
      <Flex vertical gap={12}>
        {empty && <Text type="secondary">{t(`insights.operations.${id}.empty`)}</Text>}
        <Row gutter={[8, 8]}>{figures.map(figure)}</Row>
        {extra}
      </Flex>
    </Card>
  );

  const n = (v: number): string => v.toLocaleString('en-MY');

  const body = (d: OperationsSummary): ReactNode => {
    const s = d.sales;
    const w = d.workshop;
    const till = d.till;
    const sv = d.service;
    const reg = (stage: 'AP' | 'PUSPAKOM' | 'JPJ'): Figure => {
      const gate = d.registration.find((g) => g.stage === stage) ?? { waiting: 0, overdue: 0 };
      return {
        id: `reg-${stage}`,
        label: t(`insights.operations.registration.${stage}`),
        value: n(gate.waiting),
        line: t('insights.operations.registration.overdue', { count: gate.overdue }),
        metric: `REGISTRATION_${stage}`,
        warn: gate.overdue > 0,
      };
    };
    return (
      <Flex vertical gap={12}>
        {section(
          'sales',
          [
            { id: 'leads', label: t('insights.operations.sales.leads'), value: n(s.leadsCreated), metric: 'LEADS_CREATED' },
            {
              id: 'first-contact',
              label: t('insights.operations.sales.pastFirstContact'),
              value: n(s.leadsPastFirstContact),
              metric: 'LEADS_PAST_FIRST_CONTACT',
              warn: true,
            },
            {
              id: 'next-action',
              label: t('insights.operations.sales.pastNextAction'),
              value: n(s.leadsPastNextAction),
              metric: 'LEADS_PAST_NEXT_ACTION',
              warn: true,
            },
            { id: 'quotes', label: t('insights.operations.sales.quotations'), value: n(s.quotationsSent), metric: 'QUOTATIONS_SENT' },
            { id: 'confirmed', label: t('insights.operations.sales.confirmed'), value: n(s.bookingsConfirmed), metric: 'BOOKINGS_CONFIRMED' },
            { id: 'open-bookings', label: t('insights.operations.sales.openBookings'), value: n(s.bookingsOpen), metric: 'BOOKINGS_OPEN' },
            { id: 'delivered', label: t('insights.operations.sales.delivered'), value: n(s.bikesDelivered), metric: 'BIKES_DELIVERED' },
            {
              id: 'ac-sold',
              label: t('insights.operations.sales.autocountSold'),
              value: n(s.autocountBikesSold),
              line:
                s.autocountAsOf === null
                  ? t('insights.overview.notRead')
                  : t('insights.operations.sales.autocountLine', { at: formatDateTime(s.autocountAsOf) }),
            },
          ],
          s.leadsCreated + s.bookingsOpen + s.bikesDelivered === 0,
          s.leadsBySource.length > 0 ? (
            <Flex gap={4} wrap>
              {s.leadsBySource.map((src) => (
                <Tag key={src.key}>
                  {humanise(src.key)} {n(src.count)}
                </Tag>
              ))}
            </Flex>
          ) : undefined,
        )}
        {section(
          'registration',
          [reg('AP'), reg('PUSPAKOM'), reg('JPJ')],
          d.registration.every((g) => g.waiting === 0),
        )}
        {section(
          'workshop',
          [
            { id: 'jobs-open', label: t('insights.operations.workshop.open'), value: n(w.open), metric: 'JOBS_OPEN',
              line: t('insights.operations.workshop.openLine', {
                waiting: w.waiting, inWork: w.inWork, parts: w.waitingPartsOrApproval, ready: w.billToClose,
              }) },
            { id: 'jobs-stuck', label: t('insights.operations.workshop.over3Days'), value: n(w.openOver3Days), metric: 'JOBS_OPEN_OVER_3_DAYS', warn: true },
            { id: 'jobs-closed', label: t('insights.operations.workshop.closed'), value: n(w.closed), metric: 'JOBS_CLOSED' },
            { id: 'comebacks', label: t('insights.operations.workshop.comebacks'), value: n(w.comebacks), metric: 'JOBS_COMEBACK', warn: true },
          ],
          w.open + w.closed === 0,
          w.technicians.length > 0 ? (
            <Flex vertical gap={2}>
              <Text strong>{t('insights.operations.workshop.hours')}</Text>
              {w.technicians.map((tech) => (
                <Flex key={tech.name} justify="space-between" style={{ maxWidth: 360 }}>
                  <Text>{tech.name}</Text>
                  <Text className="tabular">{t('insights.operations.workshop.hoursValue', { hours: tech.hours })}</Text>
                </Flex>
              ))}
            </Flex>
          ) : undefined,
        )}
        {section(
          'till',
          [
            { id: 'requests', label: t('insights.operations.till.requests'), value: n(till.partsRequestsWaiting), metric: 'PARTS_REQUESTS_WAITING' },
            { id: 'issued', label: t('insights.operations.till.issued'), value: formatMoney(till.invoicesIssued.total), metric: 'INVOICES_ISSUED',
              line: t('insights.operations.till.invoices', { count: till.invoicesIssued.count }) },
            { id: 'unpaid', label: t('insights.operations.till.unpaid'), value: formatMoney(till.unpaid.total), metric: 'INVOICES_UNPAID',
              line: t('insights.operations.till.invoices', { count: till.unpaid.count }) },
            { id: 'handover', label: t('insights.operations.till.notHandedOver'), value: n(till.notHandedOver), metric: 'DOCUMENTS_NOT_HANDED_OVER', warn: true },
          ],
          till.invoicesIssued.count + till.unpaid.count + till.partsRequestsWaiting === 0,
        )}
        {section(
          'service',
          [
            { id: 'cases', label: t('insights.operations.service.open'), value: n(sv.casesOpen), metric: 'CASES_OPEN' },
            { id: 'late', label: t('insights.operations.service.late'), value: n(sv.casesLate), metric: 'CASES_LATE', warn: true },
            { id: 'reminders', label: t('insights.operations.service.reminders'), value: n(sv.remindersDue), metric: 'REMINDERS_DUE' },
          ],
          sv.casesOpen + sv.remindersDue === 0,
        )}
      </Flex>
    );
  };

  return (
    <Flex vertical gap={12}>
      <Flex justify="space-between" align="center" gap={12} wrap>
        <Text type="secondary">
          {query.data === undefined
            ? ''
            : t('insights.operations.period', {
                from: formatDate(query.data.from),
                to: formatDate(query.data.to),
                at: formatDateTime(query.data.asOf),
              })}
        </Text>
        {canViewAll && (
          <Select<string>
            aria-label={t('insights.operations.branch')}
            value={branch ?? ''}
            onChange={(v) => {
              setBranch(v === '' ? null : v);
            }}
            style={{ minWidth: 200 }}
            options={[
              { value: '', label: t('insights.operations.allBranches') },
              ...(query.data?.branches ?? []).map((b) => ({ value: b.uid, label: b.name })),
            ]}
          />
        )}
      </Flex>
      {query.isError ? (
        <Text type="danger">{t('insights.loadFailed')}</Text>
      ) : query.data === undefined ? (
        <Skeleton active />
      ) : (
        body(query.data)
      )}
      <OperationsRowsDrawer
        metric={open?.metric ?? null}
        branch={branch}
        title={open?.title ?? ''}
        onClose={() => {
          setOpen(null);
        }}
      />
    </Flex>
  );
}
